/**
 * Guest cart drawer — Foodpanda-style cart; checkout continues on /checkout.
 */
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fetchRestaurantDetails } from '../../services/api/restaurantService';
import { useGuestCart } from '../hooks/useGuestCart';
import { useGuestCartStore } from '../store/useGuestCartStore';
import { formatMoney, toNumber } from '../utils/money';
import {
  isGeneratedFallbackImageUrl,
  itemColorStyle,
  itemHasBackendImage,
  itemInitialFromLabel,
  readableTextOn,
} from '../utils/itemVisuals';
import {
  buildDefaultLineInputFromMenuItem,
  itemKeyForCart,
  labelRestaurantItem,
  sortItemsForAddonRail,
} from '../utils/menuItemQuickAdd';

function QtyStepper({ qty, onDec, onInc, disabled }) {
  return (
    <div className="gc-stepper" role="group" aria-label="Quantity">
      <button type="button" className="gc-stepper__btn" onClick={onDec} disabled={disabled} aria-label="Decrease quantity">
        −
      </button>
      <div className="gc-stepper__qty" aria-label={`Quantity ${qty}`}>
        {qty}
      </div>
      <button type="button" className="gc-stepper__btn" onClick={onInc} disabled={disabled} aria-label="Increase quantity">
        +
      </button>
    </div>
  );
}

function lineDescription(line) {
  const parts = [];
  if (line?.variant?.name) parts.push(line.variant.name);
  if (Array.isArray(line?.addons) && line.addons.length) {
    line.addons.forEach((a) => {
      if (!a?.name) return;
      const n = Math.max(1, Number(a?.qty ?? 1));
      parts.push(n > 1 ? `${a.name} ×${n}` : a.name);
    });
  }
  return parts.join(', ') || ' ';
}

const CartLine = memo(function CartLine({ line, onInc, onDec, onRemove, onNotes, pricing }) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [imageBroken, setImageBroken] = useState(false);
  const hasNotes = Boolean(line?.notes && String(line.notes).trim());
  const lineColor = String(line?.imageColor || '').trim();
  const lineInitial = line?.imageInitial || itemInitialFromLabel(line?.name || 'Item');
  const lineColorStyle = lineColor ? { backgroundColor: lineColor, color: readableTextOn(lineColor) } : undefined;
  const hasLineImage = Boolean(line?.imageUrl) && !isGeneratedFallbackImageUrl(line?.imageUrl) && !imageBroken;

  return (
    <div className="gc-line">
      {hasLineImage ? (
        <img className="gc-line__img" src={line.imageUrl} alt="" loading="lazy" onError={() => setImageBroken(true)} />
      ) : (
        <div className="gc-line__img gc-line__img--color" style={lineColorStyle} aria-hidden="true">
          <span className="gc-line__initial">{lineInitial}</span>
        </div>
      )}
      <div className="gc-line__main">
        <div className="gc-line__top">
          <div className="gc-line__name">{line?.name || 'Item'}</div>
          <button type="button" className="gc-line__remove" onClick={onRemove} aria-label="Remove item">
            ×
          </button>
        </div>
        <div className="gc-line__desc">{lineDescription(line)}</div>
        <div className="gc-line__row">
          <div className="gc-line__price">{formatMoney(pricing.total)}</div>
          <QtyStepper qty={line?.qty || 1} onDec={onDec} onInc={onInc} />
        </div>
        <div className="gc-line__notes">
          <button type="button" className="gc-link" onClick={() => setNotesOpen((v) => !v)} aria-expanded={notesOpen}>
            {hasNotes ? 'Edit note' : 'Add note'}
          </button>
          {notesOpen ? (
            <textarea
              className="gc-notes"
              rows={2}
              value={line?.notes || ''}
              placeholder="Any instructions? e.g. no onions"
              onChange={(e) => onNotes(e.target.value)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default function CartDrawer() {
  const navigate = useNavigate();
  const { restaurant, lines, totals, ui, actions, getLinePricing, hasItems } = useGuestCart();
  const cartActions = useGuestCartStore((s) => s.actions);
  const [addonItems, setAddonItems] = useState([]);
  const [addonLoading, setAddonLoading] = useState(false);
  const [brokenAddonImageIds, setBrokenAddonImageIds] = useState(() => new Set());
  const [summaryOpen, setSummaryOpen] = useState(false);
  const lineIdsKey = useMemo(() => lines.map((l) => l.itemId).join('|'), [lines]);
  const markAddonImageBroken = useCallback((id) => {
    if (!id) return;
    setBrokenAddonImageIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const close = useCallback(() => actions.closeDrawer(), [actions]);

  useEffect(() => {
    if (!ui.isDrawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') actions.closeDrawer();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [ui.isDrawerOpen, actions]);

  useEffect(() => {
    if (!ui.isDrawerOpen || !restaurant?.id || !hasItems) {
      setAddonItems([]);
      return undefined;
    }
    const ac = new AbortController();
    setAddonLoading(true);
    (async () => {
      try {
        const res = await fetchRestaurantDetails(restaurant.id, { signal: ac.signal });
        const cartIds = lines.map((l) => String(l.itemId));
        const sorted = sortItemsForAddonRail(res.items, cartIds);
        setAddonItems(sorted);
      } catch {
        setAddonItems([]);
      } finally {
        setAddonLoading(false);
      }
    })();
    return () => ac.abort();
  }, [ui.isDrawerOpen, restaurant?.id, hasItems, lineIdsKey, lines]);

  const handleContinueCheckout = useCallback(() => {
    if (!hasItems) {
      toast.error('Your cart is empty.');
      return;
    }
    actions.closeDrawer();
    navigate('/checkout');
  }, [hasItems, actions, navigate]);

  const onClear = useCallback(() => {
    if (!hasItems) return;
    actions.clearCart();
    toast.success('Cart cleared');
  }, [actions, hasItems]);

  const linesWithPricing = useMemo(
    () => lines.map((l) => ({ line: l, pricing: getLinePricing(l) })),
    [lines, getLinePricing]
  );

  const quickAdd = useCallback(
    (item) => {
      if (!restaurant?.id) return;
      const input = buildDefaultLineInputFromMenuItem(item);
      if (!input) {
        toast('Customize this item from the restaurant menu.', { icon: 'ℹ️' });
        return;
      }
      const res = cartActions.addOrIncLine({
        restaurant: { id: restaurant.id, name: restaurant.name },
        lineInput: input,
      });
      if (res?.type === 'needs_confirm_replace') {
        toast('Your cart has items from another restaurant.', { icon: '⚠️' });
        return;
      }
      if (res?.type === 'ignored') return;
      toast.success('Added');
    },
    [restaurant, cartActions]
  );

  return (
    <div className={`gc-drawer ${ui.isDrawerOpen ? 'is-open' : ''}`} aria-hidden={!ui.isDrawerOpen}>
      <div className="gc-drawer__backdrop" onMouseDown={close} role="presentation" />
      <aside className="gc-drawer__panel" role="dialog" aria-modal="true" aria-label="Shopping cart">
        <header className="gc-drawer__head">
          <button type="button" className="gc-drawer__close-fab" onClick={close} aria-label="Close cart">
            ×
          </button>
          <div className="gc-drawer__head-center">
            <span className="gc-drawer__cart-title">Cart</span>
            <span className="gc-drawer__cart-underline" aria-hidden="true" />
          </div>
          <span className="gc-drawer__head-spacer" aria-hidden="true" />
        </header>

        {restaurant?.name ? <div className="gc-drawer__rest">{restaurant.name}</div> : null}

        <div className="gc-drawer__body">
          {!hasItems ? (
            <div className="gc-empty">
              <div className="gc-empty__icon" aria-hidden="true">
                🛒
              </div>
              <div className="gc-empty__title">Your cart is empty</div>
              <div className="gc-empty__sub">Add something tasty from the menu to get started.</div>
            </div>
          ) : (
            <>
              <h3 className="gc-section-label">Your items</h3>
              <div className="gc-lines">
                {linesWithPricing.map(({ line, pricing }) => (
                  <CartLine
                    key={line.lineId}
                    line={line}
                    pricing={pricing}
                    onInc={() => actions.incQty(line.lineId)}
                    onDec={() => {
                      const next = (line.qty || 1) - 1;
                      if (next <= 0) actions.removeLine(line.lineId);
                      else actions.decQty(line.lineId);
                    }}
                    onRemove={() => {
                      actions.removeLine(line.lineId);
                      toast.success('Removed');
                    }}
                    onNotes={(text) => actions.setLineNotes(line.lineId, text)}
                  />
                ))}
              </div>

              {addonLoading || addonItems.length ? (
                <>
                  <h3 className="gc-section-label gc-section-label--addons">Add more items</h3>
                  <div className="gc-addons-rail" role="list">
                    {addonLoading && !addonItems.length ? (
                      <div className="gc-addons-hint">Loading suggestions…</div>
                    ) : (
                      addonItems.map((it) => {
                        const k = itemKeyForCart(it);
                        const price = formatMoney(toNumber(it.price, 0));
                        const hasAddonImage = itemHasBackendImage(it) && !brokenAddonImageIds.has(k);
                        const addonInitial = itemInitialFromLabel(labelRestaurantItem(it) || 'Item');
                        return (
                          <div key={k} className="gc-addon-card" role="listitem">
                            <div className="gc-addon-card__visual">
                              {hasAddonImage ? (
                                <img
                                  src={it.item_photo_url}
                                  alt=""
                                  className="gc-addon-card__img"
                                  loading="lazy"
                                  onError={() => markAddonImageBroken(k)}
                                />
                              ) : (
                                <div className="gc-addon-card__img gc-addon-card__img--color" style={itemColorStyle(it)} aria-hidden="true">
                                  <span className="gc-addon-card__initial">{addonInitial}</span>
                                </div>
                              )}
                              <button type="button" className="gc-addon-card__add" onClick={() => quickAdd(it)} aria-label={`Add ${labelRestaurantItem(it)}`}>
                                +
                              </button>
                            </div>
                            <div className="gc-addon-card__name">{labelRestaurantItem(it) || 'Item'}</div>
                            <div className="gc-addon-card__price">{price}</div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>

        {hasItems ? (
          <footer className="gc-drawer__foot">
            <div className="gc-total-row">
              <div>
                <div className="gc-total-row__label">Total (incl. fees and tax)</div>
                <button type="button" className="gc-see-summary" onClick={() => setSummaryOpen((v) => !v)} aria-expanded={summaryOpen}>
                  See summary
                </button>
              </div>
              <div className="gc-total-row__amount">{formatMoney(totals.total)}</div>
            </div>

            {summaryOpen ? (
              <div className="gc-summary gc-summary--inline">
                <div className="gc-summary__row">
                  <div className="gc-summary__label">Subtotal</div>
                  <div className="gc-summary__val">{formatMoney(totals.subtotal)}</div>
                </div>
                {totals.discount > 0 ? (
                  <div className="gc-summary__row">
                    <div className="gc-summary__label">Discount</div>
                    <div className="gc-summary__val gc-summary__val--neg">− {formatMoney(totals.discount)}</div>
                  </div>
                ) : null}
                {totals.tax > 0 ? (
                  <div className="gc-summary__row">
                    <div className="gc-summary__label">Estimated tax</div>
                    <div className="gc-summary__val">{formatMoney(totals.tax)}</div>
                  </div>
                ) : null}
                {totals.delivery > 0 ? (
                  <div className="gc-summary__row">
                    <div className="gc-summary__label">Delivery fee</div>
                    <div className="gc-summary__val">{formatMoney(totals.delivery)}</div>
                  </div>
                ) : null}
              </div>
            ) : null}

            <button type="button" className="gc-btn-checkout" onClick={handleContinueCheckout}>
              Continue to checkout
            </button>
            <button type="button" className="gc-clear-link" onClick={onClear}>
              Clear cart
            </button>
          </footer>
        ) : null}
      </aside>
    </div>
  );
}
